import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Language } from '../types/language';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

const API_BASE = getApiBaseUrl();

interface FooterProps {
  language: Language;
}

function relTime(ms: number | null, isFa: boolean): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const sec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (sec < 60) return isFa ? `${sec} ثانیه پیش` : `${sec}s ago`;
  if (sec < 3600) return isFa ? `${Math.floor(sec / 60)} دقیقه پیش` : `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) return isFa ? `${Math.floor(sec / 3600)} ساعت پیش` : `${Math.floor(sec / 3600)}h ago`;
  return isFa ? `${Math.floor(sec / 86400)} روز پیش` : `${Math.floor(sec / 86400)}d ago`;
}

export const Footer: React.FC<FooterProps> = ({ language }) => {
  const isFa = language === 'fa';
  const [lastIngestUtc, setLastIngestUtc] = useState<number | null>(null);
  const [ingestStatus, setIngestStatus] = useState<string | null>(null);
  const [mlDatasource, setMlDatasource] = useState<string | null>(null);
  const [mlFetchState, setMlFetchState] = useState<'loading' | 'ok' | 'fail'>('loading');

  useEffect(() => {
    let cancel = false;
    async function load() {
      setMlFetchState('loading');
      try {
        const [stRes, mlRes] = await Promise.all([
          fetch(`${API_BASE}/data/status`),
          fetch('/api/ml/health?hours=24'),
        ]);
        const st = stRes.ok ? await stRes.json() : null;
        if (cancel) return;
        if (st?.lastIngestUtc != null) setLastIngestUtc(Number(st.lastIngestUtc));
        if (st?.ingestStatus) setIngestStatus(String(st.ingestStatus));
        if (mlRes.ok) {
          const ml = await mlRes.json();
          if (cancel) return;
          if (ml?.ml_datasource) setMlDatasource(String(ml.ml_datasource));
          else setMlDatasource(null);
          setMlFetchState('ok');
        } else {
          setMlDatasource(null);
          setMlFetchState('fail');
        }
      } catch {
        if (!cancel) {
          setIngestStatus('error');
          setMlFetchState('fail');
          setMlDatasource(null);
        }
      }
    }
    void load();
    const id = setInterval(load, 60_000);
    return () => {
      cancel = true;
      clearInterval(id);
    };
  }, []);

  const ingestLabel =
    ingestStatus === 'healthy'
      ? isFa
        ? 'سالم'
        : 'healthy'
      : ingestStatus === 'stale'
        ? isFa
          ? 'قدیمی'
          : 'stale'
        : ingestStatus === 'error'
          ? isFa
            ? 'خطا'
            : 'error'
          : '—';

  const mlLine =
    mlFetchState === 'loading'
      ? isFa
        ? 'AI · در حال بررسی…'
        : 'AI · checking…'
      : mlFetchState === 'fail'
        ? isFa
          ? 'AI · در دسترس نیست'
          : 'AI · unavailable'
        : isFa
          ? `AI · سری ${mlDatasource ?? 'merit-nt'}`
          : 'AI using same series as main chart';

  const mlTitle =
    mlFetchState === 'ok'
      ? isFa
        ? 'مدل ML روی merit-nt آموزش دیده — همان سریِ نمودار اتصال'
        : 'ML model trained on merit-nt — same series as the connectivity chart'
      : undefined;

  return (
    <footer className="mt-0 border-t border-ir-border bg-ir-bg">
      <div className="mx-auto max-w-[1440px] px-3 py-5 sm:px-5">
        <div className="flex flex-col items-stretch justify-between gap-4 text-[10px] uppercase tracking-section text-ir-muted sm:flex-row sm:items-start">
          <div className="text-center sm:text-left">
            <Link
              to="/methodology"
              className="inline-block font-sans text-xs normal-case tracking-normal text-ir-blue hover:text-ir-text hover:underline"
            >
              {isFa ? 'چگونه اندازه می‌گیریم →' : 'How we measure →'}
            </Link>
            <p className="mt-2 font-mono text-[10px] normal-case tracking-normal text-ir-faint">
              {isFa ? '© IRINTER · مانیتور اینترنت ایران' : '© IRINTER · Iran Internet Monitor'}
            </p>
          </div>
          <p className="text-ir-muted font-sans normal-case tracking-normal text-center sm:text-right max-w-xl leading-relaxed">
            {isFa ? 'به‌روزرسانی دوره‌ای · قابل استناد برای گزارش' : 'Periodic refresh · citation-grade reporting'}
            <span className="block mt-1 font-mono text-[9px] text-ir-faint">
              {isFa ? 'آخرین همگام‌سازی' : 'Last sync'}: {relTime(lastIngestUtc, isFa)} ·{' '}
              {isFa ? 'خط لولهٔ داده' : 'Data pipeline'}: {ingestLabel === 'healthy' ? (isFa ? 'درست' : 'OK') : ingestLabel} ·{' '}
              <span
                className={mlFetchState === 'fail' ? 'text-ir-amber' : ''}
                title={mlTitle ?? (isFa ? undefined : 'ML model trained on merit-nt — same series as the connectivity chart')}
              >
                {mlLine}
              </span>
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
};
